import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, Check, X, RefreshCw, DollarSign, Coins } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

/**
 * ReferralsAdminTab — admin tracks all user-to-user invites and approves/denies.
 * Also toggles per-user 'cash-eligible' flag (creators who earn cash instead of points).
 */
export default function ReferralsAdminTab() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, denied: 0, cash_pending_usd: 0, cash_paid_usd: 0 });
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get(`/admin/referrals?status=${status}`),
        api.get(`/admin/referrals/stats`),
      ]);
      setRows(r.data || []); setStats(s.data || {});
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const decide = async (r, approve) => {
    try {
      await api.post(`/admin/referrals/${r.id}/${approve ? "approve" : "deny"}`, { note: note[r.id] || "" });
      toast.success(approve ? `Approved — referrer credited` : "Denied");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleCash = async (r) => {
    try {
      await api.post(`/admin/referrals/users/${r.referrer_id}/cash-eligible`, { cash_eligible: !r.referrer_cash_eligible });
      toast.success(`${r.referrer_email} cash-eligibility ${!r.referrer_cash_eligible ? "enabled" : "disabled"}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const markPaid = async (r) => {
    if (!window.confirm(`Mark cash-pending as PAID for ${r.referrer_email}? This should be done AFTER the payout has actually been sent.`)) return;
    try {
      const { data } = await api.post(`/admin/referrals/users/${r.referrer_id}/mark-paid`);
      toast.success(`Marked $${data.amount.toFixed(2)} as paid`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-referrals-tab" className="space-y-4">
      <div className="flex items-center gap-2">
        <Gift className="w-5 h-5 text-tv-accent" />
        <h2 className="font-display font-black text-2xl text-white">Referrals · Invite Tracking</h2>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <MiniStat label="Pending" value={stats.pending} />
        <MiniStat label="Approved" value={stats.approved} />
        <MiniStat label="Denied" value={stats.denied} />
        <MiniStat icon={<Coins className="w-4 h-4 text-tv-accent" />} label="Cash pending" value={`$${(stats.cash_pending_usd || 0).toFixed(2)}`} />
        <MiniStat icon={<DollarSign className="w-4 h-4 text-emerald-300" />} label="Cash paid" value={`$${(stats.cash_paid_usd || 0).toFixed(2)}`} />
      </div>

      <div className="flex items-center gap-2">
        <div className="flex gap-1.5">
          {["pending", "approved", "denied", "all"].map((s) => (
            <button key={s} data-testid={`admin-ref-filter-${s}`} onClick={() => setStatus(s)} className={`text-xs font-bold px-3 py-1.5 rounded ${status === s ? "bg-tv-accent text-black" : "bg-white/5 text-white/70"}`}>{s.charAt(0).toUpperCase() + s.slice(1)}</button>
          ))}
        </div>
        <button onClick={load} className="ml-auto text-white/60 hover:text-white text-sm inline-flex items-center gap-1"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
      </div>

      {loading ? <div className="text-white/60">Loading…</div> : (
        <div className="space-y-2" data-testid="admin-referrals-list">
          {rows.length === 0 && <div className="text-white/40 py-6 text-center">No {status !== "all" ? status : ""} referrals.</div>}
          {rows.map((r) => (
            <div key={r.id} data-testid={`admin-referral-${r.id}`} className="bg-white/5 rounded-lg p-3">
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-bold">{r.invitee_name || r.invitee_email}</div>
                    <span className={`text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded ${
                      r.status === "pending" ? "bg-amber-500/25 text-amber-200" :
                      r.status === "approved" ? "bg-emerald-500/25 text-emerald-200" :
                      "bg-red-500/25 text-red-200"
                    }`}>{r.status}</span>
                    {r.referrer_cash_eligible && <span className="bg-emerald-500/20 text-emerald-200 text-[10px] font-black uppercase px-1.5 py-0.5 rounded inline-flex items-center gap-1"><DollarSign className="w-3 h-3" /> Cash</span>}
                  </div>
                  <div className="text-white/60 text-xs mt-0.5">Invitee: {r.invitee_email}</div>
                  <div className="text-white/60 text-xs">
                    Referrer: <strong>{r.referrer_email || r.referrer_name}</strong> · code <code className="text-tv-accent">{r.referral_code}</code>
                  </div>
                  <div className="text-white/50 text-[11px]">Submitted {new Date(r.created_at).toLocaleString()}</div>
                  {r.status === "approved" && (
                    <div className="text-emerald-300 text-xs mt-1">
                      Awarded {r.reward_kind === "cash" ? `$${r.awarded_cash_usd.toFixed(2)}` : `${r.awarded_points} pts`}
                    </div>
                  )}
                  {r.decision_note && <div className="text-white/50 text-[11px] italic mt-1">Note: {r.decision_note}</div>}
                </div>
                <button
                  onClick={() => toggleCash(r)}
                  data-testid={`admin-ref-toggle-cash-${r.id}`}
                  className={`text-[10px] font-black px-2 py-1 rounded ${r.referrer_cash_eligible ? "bg-emerald-500/30 text-emerald-200" : "bg-white/10 text-white/70"}`}
                  title="Toggle whether this referrer earns cash ($) instead of points"
                >
                  {r.referrer_cash_eligible ? "Cash ✓" : "Points only"}
                </button>
                {r.referrer_cash_eligible && r.status === "approved" && (
                  <button onClick={() => markPaid(r)} className="text-[10px] font-black bg-white/10 hover:bg-white/20 text-white/80 px-2 py-1 rounded">Mark paid</button>
                )}
              </div>

              {r.status === "pending" && (
                <div className="flex items-center gap-2 mt-2">
                  <input
                    data-testid={`admin-ref-note-${r.id}`}
                    placeholder="Decision note (optional)"
                    value={note[r.id] || ""}
                    onChange={(e) => setNote({ ...note, [r.id]: e.target.value })}
                    className="flex-1 bg-black/40 border border-white/10 rounded px-2 py-1 text-xs"
                  />
                  <button data-testid={`admin-ref-approve-${r.id}`} onClick={() => decide(r, true)} className="bg-emerald-500 hover:bg-emerald-400 text-black font-black text-xs px-3 py-1.5 rounded inline-flex items-center gap-1"><Check className="w-3 h-3" /> Approve</button>
                  <button data-testid={`admin-ref-deny-${r.id}`} onClick={() => decide(r, false)} className="bg-red-500/20 hover:bg-red-500/40 text-red-200 font-black text-xs px-3 py-1.5 rounded inline-flex items-center gap-1"><X className="w-3 h-3" /> Deny</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MiniStat({ icon, label, value }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded p-3">
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-black text-white/60">{icon}{label}</div>
      <div className="font-display font-black text-xl mt-1">{value}</div>
    </div>
  );
}
